import { Match } from '@reach/router';
import { IoIosArrowDown } from 'react-icons/io';
import { graphql, Link, StaticQuery } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { css } from 'styled-components';
import { useLang } from '../../hooks/useLang';
import useTheme from '../../hooks/useTheme';
import Box from '../Box/Box';
import Button from '../Button/Button';
import Image from '../Image/Image';
import Inline from '../Inline/Inline';
import MenuIcon from '../MenuIcon/MenuIcon';
import { Body1, Body2, Caption, Headline } from '../Typography/Typography';
import { HEADER_QUERY } from './__generated__/HEADER_QUERY';
import PrismicLink from '../PrismicLink/PrismicLink';
import { rem } from 'polished';
import { MegaMenu } from '../MegaMenu/MegaMenu';
import Grid from '../Grid/Grid';
import { ScrollIndicatorProgressBar } from '../ScrollIndicatorScrollBar/ScrollIndicatorScrollBar';
import { trackedEvents } from '../../../config/constants';
import { track } from '../../utils/analytics';
import SideMenu from '../SideMenu/SideMenu';
// @ts-ignore
import { AnimatediAdamButton } from '../AnimatediAdamButton';
import useViewport from '../../hooks/useViewport';
import { throttle } from 'lodash';
import RichText from '../RichText/RichText';

const headerQuery = graphql`
    query HEADER_QUERY {
        allPrismicHeader {
            nodes {
                lang
                data {
                    cta_1_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    cta_1_text
                    cta_1_tracking_event
                    cta_2_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    cta_2_text
                    cta_2_tracking_event
                    logo {
                        alt
                        url
                    }
                    cta_3_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    cta_3_text
                    cta_3_tracking_event
                    show_iadam_button
                    announcement_bar_content {
                        richText
                    }
                    iadam_button_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    logo_light {
                        alt
                        url
                    }
                    logo_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    navigations {
                        category
                        tracking_event
                        navigation_options {
                            slug
                            document {
                                ... on PrismicNavigationOptions {
                                    data {
                                        body {
                                            ... on PrismicNavigationOptionsDataBodyCategoryOptions {
                                                id
                                                primary {
                                                    subcategory
                                                }
                                                items {
                                                    icon {
                                                        url
                                                    }
                                                    option
                                                    tracking_event
                                                    description
                                                    link {
                                                        ...PRISMIC_LINK_FRAGMENT
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            ...PRISMIC_LINK_FRAGMENT
                        }
                    }
                }
            }
        }
    }
`;

interface HeaderProps {
    minimal?: boolean;
    CTA?: JSX.Element;
    showPageScrollIndicator?: boolean;
}

const useRelativeCoords = (elem, parent) => {
    if (!elem || !parent) return null;

    const parentCoords = parent?.getBoundingClientRect?.();
    const elemCoords = elem?.getBoundingClientRect?.();

    const relativeCoords = {
        width: elemCoords.width,
        height: elemCoords.height,
        top: elemCoords.top - parentCoords.top,
        right: elemCoords.right - parentCoords.right,
        bottom: elemCoords.bottom - parentCoords.bottom,
        left: elemCoords.left - parentCoords.left,
    } as ReturnType<typeof elem.getBoundingClientRect>;

    return relativeCoords;
};

const Header: React.FC<HeaderProps> = ({
    minimal,
    CTA,
    showPageScrollIndicator,
}) => {
    const lang = useLang();
    const { theme } = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [sideMenuHasBeenOpened, setSideMenuHasBeenOpened] = useState(false);
    const megaMenuOuterWrapperRef = React.useRef(null);
    const [activeTabElement, setActiveTabElement] = useState(null);
    const [activeTabName, setActiveTabName] = useState('');
    const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
    const megaMenuActiveTabCoords = useRelativeCoords(
        activeTabElement,
        megaMenuOuterWrapperRef.current
    );
    const { isXSMobile, isMobile } = useViewport();

    useEffect(() => {
        if (isMenuOpen && !sideMenuHasBeenOpened)
            setSideMenuHasBeenOpened(true);
    }, [isMenuOpen, sideMenuHasBeenOpened]);

    return (
        <StaticQuery
            query={headerQuery}
            render={({ allPrismicHeader: { nodes } }: HEADER_QUERY) => {
                const node = nodes.find((node) => node.lang === lang.value);

                if (!node) return null;

                const {
                    logo,
                    logo_light,
                    logo_link,
                    navigations,
                    cta_1_link,
                    cta_1_text,
                    cta_1_tracking_event,
                    cta_2_link,
                    cta_2_text,
                    cta_2_tracking_event,
                    cta_3_link,
                    cta_3_text,
                    cta_3_tracking_event,
                    iadam_button_link,
                    show_iadam_button,
                    announcement_bar_content,
                } = node.data;

                return (
                    <>
                        {announcement_bar_content.richText.length ? (
                            <Box
                                bg="primary.100"
                                py={rem(8)}
                                px={rem(16)}
                                textAlign="center"
                                flexGrow="none"
                            >
                                <Box
                                    css={css`
                                        p {
                                            font-size: 14px;
                                        }
                                    `}
                                >
                                    <RichText
                                        render={
                                            announcement_bar_content.richText
                                        }
                                    />
                                </Box>
                            </Box>
                        ) : null}

                        <Box
                            as="header"
                            position="sticky"
                            top="0"
                            left="0"
                            right="0"
                            zIndex={9999}
                            height="var(--header-height)"
                            bg="white"
                            display="flex"
                            alignItems="center"
                            flexGrow="none"
                            css={`
                                border-bottom: 1px solid
                                    ${theme.colors.neutral[200]};
                            `}
                        >
                            <Grid height="100%">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    minHeight="40px"
                                    width="100%"
                                    height="100%"
                                    gridColumn="1/-1"
                                >
                                    <Box
                                        textAlign="left"
                                        css={css`
                                            margin-inline-end: ${rem(32)};
                                        `}
                                        width={{
                                            _: rem(120),
                                            sm: rem(148),
                                        }}
                                        minWidth={{
                                            _: rem(120),
                                            sm: rem(148),
                                        }}
                                        minHeight={{
                                            _: rem(26),
                                            sm: rem(33),
                                        }}
                                    >
                                        <Link
                                            to="/"
                                            onClick={() => {
                                                track(
                                                    trackedEvents.clickOnAdamAiHomePage
                                                );
                                            }}
                                            css={css`
                                                display: flex;
                                            `}
                                        >
                                            <Image
                                                img={logo}
                                                height="100%"
                                                width="100%"
                                                alt={logo.alt}
                                            />
                                        </Link>
                                    </Box>

                                    <Inline
                                        gap={rem(20)}
                                        display={{
                                            _: 'none',
                                            md: 'flex',
                                        }}
                                        alignItems="center"
                                        justifyContent={{
                                            sm: 'flex-start',
                                        }}
                                        css={css`
                                            width: 100%;
                                            height: 100%;
                                            justify-content: space-between;
                                        `}
                                    >
                                        {!minimal && (
                                            <Inline
                                                as="ul"
                                                gap={{
                                                    _: rem(16),
                                                    lg: rem(24),
                                                }}
                                                display="flex"
                                                height="100%"
                                                position="relative"
                                                ref={megaMenuOuterWrapperRef}
                                            >
                                                <Box
                                                    margin="0 !important"
                                                    width="100px"
                                                    transition="transform 0.35s cubic-bezier(0, 0, 0.16, 0.96) 0s"
                                                    height="2px"
                                                    transformOrigin="left center"
                                                    transform={`translateX(${
                                                        megaMenuActiveTabCoords?.left ||
                                                        0
                                                    }px) scaleX(${
                                                        (isMegaMenuOpen &&
                                                            megaMenuActiveTabCoords?.width /
                                                                100) ||
                                                        0
                                                    })`}
                                                    backgroundColor={
                                                        theme.colors.primary
                                                            .main
                                                    }
                                                    position="absolute"
                                                    bottom={0}
                                                    left={0}
                                                />
                                                {navigations.map(
                                                    (
                                                        {
                                                            category,
                                                            tracking_event:
                                                                categoryTrackingEvent,
                                                            navigation_options,
                                                        },
                                                        i
                                                    ) => {
                                                        const isMegaMenu =
                                                            navigation_options
                                                                .document
                                                                .__typename ==
                                                            'PrismicNavigationOptions';
                                                        const megaMenuData =
                                                            navigation_options
                                                                .document.data
                                                                ?.body;
                                                        const slug =
                                                            navigation_options.slug;

                                                        if (isMegaMenu)
                                                            return (
                                                                <MegaMenu
                                                                    setIsMegaMenuOpen={
                                                                        setIsMegaMenuOpen
                                                                    }
                                                                    setActiveTabElement={
                                                                        setActiveTabElement
                                                                    }
                                                                    renderTrigger={({
                                                                        isOpen,
                                                                        toggle,
                                                                        triggerRef,
                                                                    }) => (
                                                                        <Box
                                                                            ref={
                                                                                triggerRef
                                                                            }
                                                                            display="grid"
                                                                            gridAutoFlow="column"
                                                                            gridColumnGap="4px"
                                                                            alignItems="center"
                                                                            cursor="pointer"
                                                                            tabIndex="0"
                                                                            role="button"
                                                                            height="100%"
                                                                            userSelect="none"
                                                                            onClick={() => {
                                                                                toggle();
                                                                                setActiveTabName(
                                                                                    category
                                                                                );
                                                                            }}
                                                                            {...(isMegaMenuOpen &&
                                                                                activeTabName ===
                                                                                    category && {
                                                                                    color: theme
                                                                                        .colors
                                                                                        .primary
                                                                                        .main,
                                                                                })}
                                                                            _hover={{
                                                                                color: theme
                                                                                    .colors
                                                                                    .primary
                                                                                    .main,
                                                                            }}
                                                                        >
                                                                            <Headline
                                                                                as="span"
                                                                                color="currentColor"
                                                                                transition="color 200ms ease"
                                                                                {...(isMegaMenuOpen &&
                                                                                    activeTabName ===
                                                                                        category && {
                                                                                        color: theme
                                                                                            .colors
                                                                                            .primary
                                                                                            .main,
                                                                                        fontWeight: 500,
                                                                                    })}
                                                                                onClick={() => {
                                                                                    track(
                                                                                        categoryTrackingEvent
                                                                                    );
                                                                                }}
                                                                            >
                                                                                {
                                                                                    category
                                                                                }
                                                                            </Headline>
                                                                            <Box
                                                                                size={
                                                                                    20
                                                                                }
                                                                                as={
                                                                                    IoIosArrowDown
                                                                                }
                                                                                css={css`
                                                                                    transition: transform
                                                                                            250ms
                                                                                            cubic-bezier(
                                                                                                0,
                                                                                                0,
                                                                                                0.16,
                                                                                                0.96
                                                                                            )
                                                                                            0s,
                                                                                        color
                                                                                            200ms
                                                                                            ease;
                                                                                    transform: rotate(
                                                                                        ${isOpen
                                                                                            ? -180
                                                                                            : 0}deg
                                                                                    );
                                                                                `}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                >
                                                                    {(
                                                                        close
                                                                    ) => (
                                                                        <Box
                                                                            css={css`
                                                                                display: flex;
                                                                                gap: ${rem(
                                                                                    16
                                                                                )};
                                                                            `}
                                                                        >
                                                                            {megaMenuData.map(
                                                                                ({
                                                                                    primary,
                                                                                    items,
                                                                                }) => (
                                                                                    <Box
                                                                                        css={css`
                                                                                            display: grid;
                                                                                            row-gap: ${rem(
                                                                                                24
                                                                                            )};
                                                                                        `}
                                                                                    >
                                                                                        <Caption>
                                                                                            {
                                                                                                primary.subcategory
                                                                                            }
                                                                                        </Caption>
                                                                                        <Box
                                                                                            css={css`
                                                                                                display: grid;
                                                                                                grid-template-rows: repeat(
                                                                                                    3,
                                                                                                    1fr
                                                                                                );
                                                                                                gap: ${rem(
                                                                                                    16
                                                                                                )};
                                                                                                grid-auto-flow: column;
                                                                                                grid-auto-columns: 242px;
                                                                                            `}
                                                                                        >
                                                                                            {items.map(
                                                                                                (
                                                                                                    item
                                                                                                ) => {
                                                                                                    const isActivePage =
                                                                                                        window.location.href.endsWith(
                                                                                                            item
                                                                                                                .link
                                                                                                                .document
                                                                                                                ?.data
                                                                                                                ?.slug
                                                                                                        );

                                                                                                    const hoverAndActiveStyles = css`
                                                                                                        ::before {
                                                                                                            content: '';
                                                                                                            display: block;
                                                                                                            width: calc(
                                                                                                                100% +
                                                                                                                    16px
                                                                                                            );
                                                                                                            height: calc(
                                                                                                                100% +
                                                                                                                    16px
                                                                                                            );
                                                                                                            position: absolute;
                                                                                                            left: -8px;
                                                                                                            top: -8x;
                                                                                                            background-color: ${theme
                                                                                                                .colors
                                                                                                                .primary[50]};
                                                                                                            z-index: -1;
                                                                                                            border-radius: ${theme
                                                                                                                .radii
                                                                                                                .md};
                                                                                                        }

                                                                                                        .mega-menu-item__icon,
                                                                                                        .mega-menu-item__title {
                                                                                                            color: ${theme
                                                                                                                .colors
                                                                                                                .primary
                                                                                                                .main};
                                                                                                        }
                                                                                                        .mega-menu-item__description {
                                                                                                            color: ${theme
                                                                                                                .colors
                                                                                                                .primary[1800]};
                                                                                                        }
                                                                                                    `;
                                                                                                    return (
                                                                                                        <Box
                                                                                                            as={
                                                                                                                PrismicLink
                                                                                                            }
                                                                                                            link={
                                                                                                                item.link
                                                                                                            }
                                                                                                            onClick={() => {
                                                                                                                track(
                                                                                                                    item.tracking_event
                                                                                                                );
                                                                                                                close();
                                                                                                            }}
                                                                                                            target="_self"
                                                                                                            className="mega-menu-item"
                                                                                                            css={`
                                                                                                                position: relative;
                                                                                                                a {
                                                                                                                    display: grid;
                                                                                                                    grid-template-columns: ${rem(
                                                                                                                            20
                                                                                                                        )} 1fr;
                                                                                                                    align-items: center;
                                                                                                                    grid-gap: ${rem(
                                                                                                                        8
                                                                                                                    )};
                                                                                                                    :hover {
                                                                                                                        ${hoverAndActiveStyles}
                                                                                                                    }
                                                                                                                    ${isActivePage &&
                                                                                                                    hoverAndActiveStyles}
                                                                                                                }
                                                                                                            `}
                                                                                                        >
                                                                                                            <Box
                                                                                                                className="mega-menu-item__icon"
                                                                                                                marginTop={rem(
                                                                                                                    2
                                                                                                                )}
                                                                                                                alignSelf="flex-start"
                                                                                                            >
                                                                                                                <Image
                                                                                                                    img={
                                                                                                                        item.icon
                                                                                                                    }
                                                                                                                    width={rem(
                                                                                                                        20
                                                                                                                    )}
                                                                                                                    height={rem(
                                                                                                                        20
                                                                                                                    )}
                                                                                                                />
                                                                                                            </Box>
                                                                                                            <Box>
                                                                                                                <Body1
                                                                                                                    className="mega-menu-item__title"
                                                                                                                    color={
                                                                                                                        theme
                                                                                                                            .colors
                                                                                                                            .primary[1800]
                                                                                                                    }
                                                                                                                    marginBottom={rem(
                                                                                                                        4
                                                                                                                    )}
                                                                                                                >
                                                                                                                    {
                                                                                                                        item.option
                                                                                                                    }
                                                                                                                </Body1>
                                                                                                                <Box
                                                                                                                    as={
                                                                                                                        Body2
                                                                                                                    }
                                                                                                                    className="mega-menu-item__description"
                                                                                                                    color={
                                                                                                                        theme
                                                                                                                            .colors
                                                                                                                            .neutral[1200]
                                                                                                                    }
                                                                                                                    height={rem(
                                                                                                                        42
                                                                                                                    )}
                                                                                                                    overflow="hidden"
                                                                                                                >
                                                                                                                    {
                                                                                                                        item.description
                                                                                                                    }
                                                                                                                </Box>
                                                                                                            </Box>
                                                                                                        </Box>
                                                                                                    );
                                                                                                }
                                                                                            )}
                                                                                        </Box>
                                                                                    </Box>
                                                                                )
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </MegaMenu>
                                                            );

                                                        return (
                                                            <Box
                                                                as={PrismicLink}
                                                                key={i}
                                                                link={
                                                                    navigation_options
                                                                }
                                                                target="_self"
                                                            >
                                                                <Match
                                                                    path={`/${slug}`}
                                                                >
                                                                    {({
                                                                        match,
                                                                    }) => (
                                                                        <Headline
                                                                            as={
                                                                                Box
                                                                            }
                                                                            accent={
                                                                                match
                                                                            }
                                                                            height="100%"
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            transition="color 200ms ease"
                                                                            _hover={{
                                                                                color: theme
                                                                                    .colors
                                                                                    .primary
                                                                                    .main,
                                                                            }}
                                                                            onClick={() =>
                                                                                track(
                                                                                    categoryTrackingEvent
                                                                                )
                                                                            }
                                                                        >
                                                                            {
                                                                                category
                                                                            }
                                                                        </Headline>
                                                                    )}
                                                                </Match>
                                                            </Box>
                                                        );
                                                    }
                                                )}
                                                {show_iadam_button && (
                                                    <AnimatediAdamButton
                                                        link={iadam_button_link}
                                                    />
                                                )}
                                            </Inline>
                                        )}
                                        <Inline
                                            gap={{
                                                _: rem(16),
                                                lg: rem(20),
                                            }}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            whiteSpace="nowrap"
                                            marginLeft="auto"
                                        >
                                            {CTA || (
                                                <>
                                                    <PrismicLink
                                                        link={cta_1_link}
                                                        target="_self"
                                                    >
                                                        <Button
                                                            size="md"
                                                            variant="ghost-blue"
                                                            trackingEvent={
                                                                cta_1_tracking_event
                                                            }
                                                        >
                                                            {cta_1_text}
                                                        </Button>
                                                    </PrismicLink>
                                                    <PrismicLink
                                                        link={cta_2_link}
                                                        target="_self"
                                                    >
                                                        <Button
                                                            size="md"
                                                            variant="primary"
                                                            trackingEvent={
                                                                cta_2_tracking_event
                                                            }
                                                        >
                                                            {cta_2_text}
                                                        </Button>
                                                    </PrismicLink>
                                                    {cta_3_text && (
                                                        <PrismicLink
                                                            link={cta_3_link}
                                                            target="_self"
                                                        >
                                                            <Button
                                                                size="md"
                                                                trackingEvent={
                                                                    cta_3_tracking_event
                                                                }
                                                            >
                                                                {cta_3_text}
                                                            </Button>
                                                        </PrismicLink>
                                                    )}
                                                </>
                                            )}
                                        </Inline>
                                    </Inline>

                                    <Box
                                        display={{
                                            _: 'flex',
                                            md: 'none',
                                        }}
                                        alignItems="center"
                                        marginLeft="auto"
                                        gap={rem(8)}
                                    >
                                        <AnimatediAdamButton
                                            onlyLogo={isXSMobile}
                                            link={iadam_button_link}
                                        />
                                        <Box
                                            marginRight={{
                                                _: rem(4),
                                                lg: rem(16),
                                            }}
                                        >
                                            <Button
                                                as={PrismicLink}
                                                link={cta_2_link}
                                                trackingEvent={
                                                    cta_2_tracking_event
                                                }
                                                size={isXSMobile ? 'sm' : 'md'}
                                            >
                                                {cta_2_text}
                                            </Button>
                                        </Box>

                                        <MenuIcon
                                            onOpen={() => setIsMenuOpen(true)}
                                            onClose={() => setIsMenuOpen(false)}
                                        />
                                        {sideMenuHasBeenOpened && (
                                            <SideMenu
                                                navigations={navigations}
                                                isOpen={isMenuOpen}
                                                logoLink={{
                                                    ...logo_link,
                                                    url: '/',
                                                }}
                                                logo={logo_light}
                                                onClose={() => {
                                                    setIsMenuOpen(false);
                                                }}
                                                onClick={() => {
                                                    track(
                                                        trackedEvents.clickOnAdamAiHomePage
                                                    );
                                                }}
                                                ctaGroup={
                                                    <>
                                                        <PrismicLink
                                                            link={cta_2_link}
                                                        >
                                                            <Button
                                                                fullWidth
                                                                size="lg"
                                                                trackingEvent={
                                                                    cta_2_tracking_event
                                                                }
                                                            >
                                                                {cta_2_text}
                                                            </Button>
                                                        </PrismicLink>
                                                        <PrismicLink
                                                            link={cta_1_link}
                                                        >
                                                            <Button
                                                                fullWidth
                                                                size="lg"
                                                                variant="secondary"
                                                                isLight
                                                                trackingEvent={
                                                                    cta_1_tracking_event
                                                                }
                                                            >
                                                                {cta_1_text}
                                                            </Button>
                                                        </PrismicLink>
                                                    </>
                                                }
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                            {showPageScrollIndicator && (
                                <ScrollIndicatorProgressBar />
                            )}
                        </Box>
                    </>
                );
            }}
        />
    );
};

export default Header;
